export const RECENT_REQUEST_IDS_STORAGE_KEY = 'sumrise.recentRequestIds';
export const RECENT_REQUEST_IDS_LIMIT = 20;
export const RECENT_CLIENT_EVENTS_LIMIT = 10;
export const CLIENT_EVENT_TEXT_LIMIT = 300;

const recentConsoleErrors = [];
const recentFailedFetches = [];
let consoleCaptureInstalled = false;

function boundedText(value, limit = CLIENT_EVENT_TEXT_LIMIT) {
  return String(value || '').slice(0, limit);
}

function addBoundedEvent(target, event) {
  target.unshift(event);
  target.splice(RECENT_CLIENT_EVENTS_LIMIT);
}

function errorMetadata(value) {
  if (!(value instanceof Error)) {
    return { error_name: 'ConsoleError' };
  }
  const stackFrames = String(value.stack || '')
    .split('\n')
    .slice(1, 4)
    .join('\n');
  return {
    error_name: boundedText(value.name || 'Error', 80),
    stack: boundedText(stackFrames),
  };
}

export function recordConsoleError(...args) {
  addBoundedEvent(recentConsoleErrors, {
    ...errorMetadata(args.find((value) => value instanceof Error)),
    timestamp: new Date().toISOString(),
  });
}

export function installClientErrorCapture() {
  if (consoleCaptureInstalled || typeof console === 'undefined') {
    return;
  }
  const originalConsoleError = console.error;
  console.error = (...args) => {
    recordConsoleError(...args);
    originalConsoleError.apply(console, args);
  };
  consoleCaptureInstalled = true;
}

export function recordFailedFetch(path, status, method = 'GET') {
  let pathname = '';
  try {
    pathname = new URL(String(path || ''), window.location.origin).pathname;
  } catch (_error) {
    pathname = String(path || '').split(/[?#]/)[0];
  }
  addBoundedEvent(recentFailedFetches, {
    path: boundedText(pathname),
    status: status !== null && status !== undefined && Number.isFinite(Number(status))
      ? Number(status)
      : null,
    method: boundedText(String(method || 'GET').toUpperCase(), 12),
    timestamp: new Date().toISOString(),
  });
}

export function getRecentClientEvents() {
  return {
    console_errors: recentConsoleErrors.map((entry) => ({ ...entry })),
    failed_fetches: recentFailedFetches.map((entry) => ({ ...entry })),
  };
}

export function clearRecentClientEventsForTests() {
  recentConsoleErrors.splice(0);
  recentFailedFetches.splice(0);
}

export function getAppEnvironment() {
  return boundedText(
    process.env.REACT_APP_APP_ENVIRONMENT
      || process.env.REACT_APP_ENVIRONMENT
      || 'unknown',
    80
  ) || 'unknown';
}

export function getFrontendBuildSha() {
  return boundedText(
    process.env.REACT_APP_BUILD_SHA
      || process.env.REACT_APP_GIT_SHA
      || process.env.REACT_APP_COMMIT_SHA
      || 'unknown',
    160
  ) || 'unknown';
}

export function isBetaHelpEnabled() {
  // Feedback is a core authenticated Beta journey. A missing build-time
  // variable must not silently remove the only in-app reporting route.
  // Explicit configuration is parsed strictly so an operator typo cannot
  // accidentally defeat the emergency off-switch during an incident.
  const configuredValue = process.env.REACT_APP_BETA_HELP_ENABLED;
  if (configuredValue === undefined) {
    return true;
  }
  return String(configuredValue).trim().toLowerCase() === 'true';
}

function getSessionStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.sessionStorage || null;
}

function normalizeRequestId(value) {
  const text = String(value || '').trim();
  if (!text || text.length > 160) {
    return '';
  }
  return text;
}

export function getRecentRequestIds() {
  const storage = getSessionStorage();
  if (!storage) {
    return [];
  }
  try {
    const parsed = JSON.parse(storage.getItem(RECENT_REQUEST_IDS_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((entry) => ({
        request_id: normalizeRequestId(entry?.request_id),
        timestamp: String(entry?.timestamp || ''),
      }))
      .filter((entry) => entry.request_id && entry.timestamp)
      .slice(0, RECENT_REQUEST_IDS_LIMIT);
  } catch (_error) {
    return [];
  }
}

export function recordRequestId(requestId, timestamp = new Date().toISOString()) {
  const normalized = normalizeRequestId(requestId);
  if (!normalized) {
    return [];
  }
  const storage = getSessionStorage();
  if (!storage) {
    return [];
  }
  const next = [
    { request_id: normalized, timestamp },
    ...getRecentRequestIds().filter((entry) => entry.request_id !== normalized),
  ].slice(0, RECENT_REQUEST_IDS_LIMIT);
  try {
    storage.setItem(RECENT_REQUEST_IDS_STORAGE_KEY, JSON.stringify(next));
  } catch (_error) {
    return next;
  }
  return next;
}
